import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { User, Package, MapPin, Heart, Star, MessageSquare, LayoutDashboard, BookOpen, Award, Download, CreditCard, Share2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import Header from './Header';
import Footer from './Footer';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useSettings } from '@/hooks/useProducts';
import { IS_SAAS } from '@/config/site';

export default function AccountLayout() {
  const { user, isInitialized } = useAuthStore();
  const location = useLocation();
  const { isEnabled } = useFeatureFlags();
  const { data: settingsData } = useSettings();
  const showEcommerce = isEnabled('ecommerce');
  const showCourses = isEnabled('courses');
  // Concept #2 plan subscription (e.g. a custom-app Pro tier).
  //
  // On a SaaS tenant this is the ONLY working purchase surface, so hiding it
  // until the user already has a subscription was exactly backwards — a new
  // customer had no reachable way to buy one. Show it to every SaaS customer
  // (the page renders the plan picker when they have none), and keep the
  // subscription-only behaviour everywhere else.
  const showBilling = IS_SAAS || !!user?.hasActiveSubscription;
  // Refer-a-friend already exists but was previously unreachable from the
  // account nav. Surface it whenever the referral program is enabled.
  const showReferrals = !!settingsData?.settings?.upsell_config?.referral_program?.enabled;

  const accountLinks: { label: string; href?: string; icon: any; axissOpen?: boolean }[] = [
    { label: 'Dashboard', href: '/account', icon: LayoutDashboard },
    ...(showEcommerce ? [
      { label: 'Orders', href: '/account/orders', icon: Package },
      { label: 'Downloads', href: '/account/downloads', icon: Download },
      { label: 'Addresses', href: '/account/addresses', icon: MapPin },
      { label: 'Wishlist', href: '/account/wishlist', icon: Heart },
    ] : []),
    ...(showCourses ? [
      { label: 'My Courses', href: '/account/courses', icon: BookOpen },
      { label: 'Certificates', href: '/account/certificates', icon: Award },
    ] : []),
    ...(showBilling ? [
      { label: 'Billing', href: '/account/billing', icon: CreditCard },
    ] : []),
    ...(showReferrals ? [
      { label: 'Refer a friend', href: '/account/referrals', icon: Share2 },
    ] : []),
    { label: 'Reviews', href: '/account/reviews', icon: Star },
    { label: 'Support', icon: MessageSquare, axissOpen: true },
    { label: 'Profile', href: '/account/profile', icon: User },
  ];

  if (isInitialized && !user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />
      <main id="main-content" className="flex-1 bg-surface-soft" tabIndex={-1}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-display font-bold text-ink mb-6">My Account</h1>
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar nav - tabs on mobile, sidebar on desktop */}
            <nav
              className="lg:w-56 shrink-0"
              aria-label="Account navigation"
            >
              <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
                {accountLinks.map((link) => {
                  const Icon = link.icon;
                  if (link.axissOpen) {
                    return (
                      <button
                        key={link.label}
                        data-axiss-open="chat"
                        className={cn(
                          'flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors min-h-[44px]',
                          'text-ink-soft hover:bg-surface-muted hover:text-ink'
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                        {link.label}
                      </button>
                    );
                  }
                  const isActive = location.pathname === link.href ||
                    (link.href !== '/account' && location.pathname.startsWith(link.href!));
                  return (
                    <Link
                      key={link.href}
                      to={link.href!}
                      className={cn(
                        'flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors min-h-[44px]',
                        isActive
                          ? 'bg-sage-100 text-sage-800'
                          : 'text-ink-soft hover:bg-surface-muted hover:text-ink'
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </nav>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <Outlet />
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
